/**
 * Glue between persisted WidgetConfig and the runtime aggregation adapter:
 * resolves a dataset's fields (via the enhanced /fields endpoint) and turns a
 * WidgetConfig + dashboard-level filters into a `ResolvedWidget`.
 */

import {
  resolveFieldsFromEnhancedEndpoint,
  type EnhancedFieldsResponse,
} from '../field-filter-pipeline.ts';
import type { FilterClauseContext } from '../filter-clause-builder.ts';
import { buildConfiguredFilterClauses, buildExtraFilterClauses } from '../filter-query-builder.ts';
import type { FieldOption } from '../opensearch-field-mapper.ts';
import {
  getModelTypeFromDataset,
  getCompanyFilterField,
  getSearchRequestModelType,
} from '../dataset-utils.ts';
import { AGG_LABELS } from '../aggregation-types.ts';
import { fieldsRequestFor } from '../search-requests.ts';
import type { ResolvedMeasure, ResolvedWidget } from './widget-data-adapter.ts';
import type {
  MeasureSpec,
  WidgetConfig,
  BuildFilterConfig,
  UserFilterConfig,
} from './widget-config-types.ts';
import type { Store as StoreService } from '@warp-drive/core';

/** Fetch and map the field registry for a dataset (optionally company-scoped). */
export async function resolveDatasetFields(
  store: StoreService,
  dataset: string,
  companyId?: string,
  showIndividualApplicationsOnly = true
): Promise<FieldOption[]> {
  const modelType = getSearchRequestModelType(
    getModelTypeFromDataset(dataset),
    showIndividualApplicationsOnly
  );
  // Source-kind seam (RFC 062): OpenSearch datasets hit the classic /fields
  // endpoint, SQL datasets sql-search-fields — same enhanced response shape.
  const { content } = await store.request<EnhancedFieldsResponse>(
    fieldsRequestFor(modelType, companyId)
  );
  return resolveFieldsFromEnhancedEndpoint(content);
}

/**
 * Assemble every filter clause for a widget: the dashboard-level build + user
 * filters (single-dataset only — the page passes nothing otherwise) merged with
 * the widget's own build filters, all run through the shared report-builder
 * clause assembler so semantics match the tabular builder exactly. Build
 * layouts concatenate (each AND'd at the top level, OR within its own group);
 * on a same-field collision the widget's value wins.
 */
function widgetFilterClauses(
  widget: WidgetConfig,
  dashboardBuild: BuildFilterConfig | undefined,
  dashboardUser: UserFilterConfig | undefined,
  extraFilterValues: Record<string, unknown>,
  ctx: FilterClauseContext
) {
  const widgetBuild = widget.buildFilter;
  const configured = buildConfiguredFilterClauses(
    {
      buildFilterValues: {
        ...(dashboardBuild?.filterValues ?? {}),
        ...(widgetBuild?.filterValues ?? {}),
      },
      buildFilterLayout: [
        ...(dashboardBuild?.filterLayout ?? []),
        ...(widgetBuild?.filterLayout ?? []),
      ],
      userFilterValues: dashboardUser?.filterValues ?? {},
    },
    ctx
  );
  // Appended as separate clauses, never merged into the user map, so a same-key entry ANDs.
  return [...configured, ...buildExtraFilterClauses(extraFilterValues, ctx)];
}

/** Human-readable legend label for a measure. */
function measureLabel(spec: MeasureSpec, field: FieldOption | null): string {
  if (spec.label) return spec.label;
  if (!field) return 'Count';
  return spec.aggregation === 'count'
    ? field.label
    : `${AGG_LABELS[spec.aggregation]} ${field.label}`;
}

export interface ToResolvedWidgetOptions {
  companyId?: string;
  buildFilters?: BuildFilterConfig;
  userFilters?: UserFilterConfig;
  /** Flat AND'd entries applied after every configured filter (drilldowns). */
  extraFilterValues?: Record<string, unknown>;
}

/** Map a WidgetConfig + resolved fields + dashboard filters into a ResolvedWidget. */
export function toResolvedWidget(
  widget: WidgetConfig,
  fields: FieldOption[],
  options: ToResolvedWidgetOptions = {}
): ResolvedWidget {
  const fieldByKey: Record<string, FieldOption> = {};
  for (const f of fields) fieldByKey[f.key] = f;

  const pathFor = (key: string) => {
    const baseKey = key.split('__')[0]!;
    const field = fieldByKey[baseKey];
    return field?.filterPath ?? field?.path ?? baseKey;
  };
  const ctx: FilterClauseContext = { pathFor, fieldByKey };

  const resolveMeasure = (spec: MeasureSpec): ResolvedMeasure => {
    const field = spec.fieldKey ? (fieldByKey[spec.fieldKey] ?? null) : null;
    return { id: spec.id, field, aggregation: spec.aggregation, label: measureLabel(spec, field) };
  };

  const shared = {
    modelType: getSearchRequestModelType(
      getModelTypeFromDataset(widget.dataset),
      widget.showIndividualApplicationsOnly ?? true
    ),
    filterClauses: widgetFilterClauses(
      widget,
      options.buildFilters,
      options.userFilters,
      options.extraFilterValues ?? {},
      ctx
    ),
    companyId: options.companyId,
    companyFilterField: getCompanyFilterField(widget.dataset),
    limit: widget.limit,
  };

  if (widget.widgetType === 'counter') {
    const measureField = widget.measure?.fieldKey
      ? (fieldByKey[widget.measure.fieldKey] ?? null)
      : null;
    return {
      ...shared,
      widgetType: 'counter',
      measureField,
      aggregation: widget.measure?.aggregation ?? 'count',
    };
  }

  if (widget.widgetType === 'pie') {
    return {
      ...shared,
      widgetType: 'pie',
      sliceField: widget.sliceDimension?.fieldKey
        ? fieldByKey[widget.sliceDimension.fieldKey]
        : undefined,
      sliceInterval: widget.sliceDimension?.dateInterval,
      measureField: widget.valueMeasure?.fieldKey
        ? (fieldByKey[widget.valueMeasure.fieldKey] ?? null)
        : null,
      aggregation: widget.valueMeasure?.aggregation ?? 'count',
      donut: widget.donut,
      colors: widget.format?.colors,
    };
  }

  const xField = widget.xDimension?.fieldKey ? fieldByKey[widget.xDimension.fieldKey] : undefined;
  const groupField = widget.groupDimension?.fieldKey
    ? fieldByKey[widget.groupDimension.fieldKey]
    : undefined;

  return {
    ...shared,
    widgetType: 'chart',
    xDimension: xField,
    xInterval: widget.xDimension?.dateInterval,
    groupDimension: groupField,
    bars: (widget.bars ?? []).map(resolveMeasure),
    lines: (widget.lines ?? []).map(resolveMeasure),
    barMode: widget.barMode ?? 'stacked',
    linesAxis: widget.linesAxis ?? 'secondary',
    colors: widget.format?.colors,
  };
}
